// ════════════════════════════════════════════════════════════════════
//  מקור-אמת יחיד לכללי הנגישות — בלי תלות ב-React.
//  נטען גם מ-vite.config.js (Node) לייצור סקריפט ה-FOUC, וגם בזמן ריצה,
//  כדי שלא יהיו שני עותקים של הכללים שיכולים לסטות.
//
//  ערכים בינאריים/אנומים → קלאסים על <html>.
//  ערכים מספריים (גודל טקסט, ריווח) → CSS custom properties, כדי שאפשר
//  יהיה להזיז אותם ברזולוציה עדינה עם +/− במקום מחזור בין 3 מצבים.
// ════════════════════════════════════════════════════════════════════

export const KEY = "heshbit_a11y_prefs_v2";
export const VERSION = 2;

export const DEFAULT_PREFS = {
  version: VERSION,
  contrast: "off",     // off | high | invert | mono | bw
  textScale: 100,      // 100–200, צעדים של 10
  lineScale: 100,      // 100–200
  wordSpace: 0,        // 0–8  (כל צעד 0.05em)
  letterSpace: 0,      // 0–8  (כל צעד 0.03em)
  links: false,        // הדגשת קישורים
  headings: false,     // הדגשת כותרות
  readable: false,     // גופן קריא (מחסנית מערכת, בלי webfont)
  cursor: "off",       // off | light | dark
  readingGuide: false, // סרגל קריאה שעוקב אחרי הסמן
  hideImages: false,   // הסתרת תמונות
  reduceMotion: false, // עצירת אנימציות
};

// גבולות לערכים המספריים — משמשים גם את ה-UI וגם את הבדיקות
export const RANGES = {
  textScale:   { min: 100, max: 200, step: 10, unit: "%" },
  lineScale:   { min: 100, max: 200, step: 10, unit: "%" },
  wordSpace:   { min: 0,   max: 8,   step: 1,  unit: "" },
  letterSpace: { min: 0,   max: 8,   step: 1,  unit: "" },
};

export const clampPref = (key, v) => {
  const r = RANGES[key];
  if (!r) return v;
  return Math.min(r.max, Math.max(r.min, Math.round(v / r.step) * r.step));
};

// [class, predicate(prefs), jsExpr-for-bootstrap]
export const CLASS_RULES = [
  ["a11y-links",           (p) => p.links,                  "!!p.links"],
  ["a11y-headings",        (p) => p.headings,               "!!p.headings"],
  ["a11y-readable",        (p) => p.readable,               "!!p.readable"],
  ["a11y-cursor-light",    (p) => p.cursor === "light",     "p.cursor==='light'"],
  ["a11y-cursor-dark",     (p) => p.cursor === "dark",      "p.cursor==='dark'"],
  ["a11y-hide-images",     (p) => p.hideImages,             "!!p.hideImages"],
  ["a11y-reduce-motion",   (p) => p.reduceMotion,           "!!p.reduceMotion"],
  ["a11y-contrast-high",   (p) => p.contrast === "high",    "p.contrast==='high'"],
  ["a11y-contrast-invert", (p) => p.contrast === "invert",  "p.contrast==='invert'"],
  ["a11y-contrast-mono",   (p) => p.contrast === "mono",    "p.contrast==='mono'"],
  ["a11y-contrast-bw",     (p) => p.contrast === "bw",      "p.contrast==='bw'"],
];

// [cssVar, valueFn(prefs), jsExpr-for-bootstrap] — null מסיר את המשתנה
export const VAR_RULES = [
  ["--a11y-text-scale",   (p) => (p.textScale > 100 ? p.textScale / 100 : null),
                          "p.textScale>100?p.textScale/100:null"],
  ["--a11y-line-scale",   (p) => (p.lineScale > 100 ? p.lineScale / 100 : null),
                          "p.lineScale>100?p.lineScale/100:null"],
  ["--a11y-word-space",   (p) => (p.wordSpace > 0 ? p.wordSpace * 0.05 + "em" : null),
                          "p.wordSpace>0?(p.wordSpace*0.05)+'em':null"],
  ["--a11y-letter-space", (p) => (p.letterSpace > 0 ? p.letterSpace * 0.03 + "em" : null),
                          "p.letterSpace>0?(p.letterSpace*0.03)+'em':null"],
];

export function applyPrefsToElement(prefs, el) {
  const html = el || document.documentElement;
  for (const [cls, pred] of CLASS_RULES) html.classList.toggle(cls, !!pred(prefs));
  for (const [name, valueFn] of VAR_RULES) {
    const v = valueFn(prefs);
    if (v == null) html.style.removeProperty(name);
    else html.style.setProperty(name, v);
  }
}

// סקריפט ה-FOUC — רץ סינכרונית ב-<head> לפני React, כדי שמשתמש עם ניגודיות
// גבוהה או טקסט מוגדל לא יראה הבזק של העיצוב הרגיל. נבנה מאותם הכללים.
export const A11Y_BOOTSTRAP_SCRIPT =
  `(function(){try{var raw=localStorage.getItem('${KEY}');if(!raw)return;` +
  `var p=JSON.parse(raw);if(p.version!==${VERSION})return;` +
  `var e=document.documentElement,c=e.classList,v;` +
  CLASS_RULES.map(([cls, , js]) => `c.toggle('${cls}',!!(${js}))`).join(";") + ";" +
  VAR_RULES.map(([name, , js]) =>
    `v=(${js});if(v==null){e.style.removeProperty('${name}')}else{e.style.setProperty('${name}',v)}`
  ).join("") +
  `}catch(e){}})()`;
